import React, { useRef, useCallback, useState, useEffect } from 'react';
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link,
  Image,
  Variable,
  Type,
  Palette,
  Undo2,
  Redo2,
  Eye,
  Code,
} from 'lucide-react';
import { TEMPLATE_VARIABLES, generateBrandedEmail, replaceVariables, buildVariables } from '../lib/emailService';

/**
 * Rich Email Editor with toolbar
 * Uses contentEditable for WYSIWYG editing with custom toolbar controls.
 * Supports variable insertion, formatting, and branded preview.
 */
const EmailEditor = ({
  value = '',
  onChange,
  onSubjectChange,
  subject = '',
  previewEvent = null,
  previewVenue = null,
  minHeight = 300,
}) => {
  const editorRef = useRef(null);
  const [showVariables, setShowVariables] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontSize, setShowFontSize] = useState(false);
  const [activeFormats, setActiveFormats] = useState({});

  // Sync value into editor on mount or when value changes externally
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
    updateActiveFormats();
  }, []);

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
    });
  }, []);

  const insertVariable = useCallback((variableKey) => {
    const tag = `<span class="email-variable" contenteditable="false" style="background-color:#dbeafe;color:#1e40af;padding:2px 6px;border-radius:4px;font-size:13px;font-family:monospace;cursor:default;display:inline-block;margin:0 2px;">{${variableKey}}</span>&nbsp;`;
    document.execCommand('insertHTML', false, tag);
    editorRef.current?.focus();
    handleInput();
    setShowVariables(false);
  }, [handleInput]);

  const insertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  const insertImage = useCallback(() => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  }, [execCommand]);

  const colors = [
    '#1A5C3A', '#c8a96e', '#333333', '#1e40af', '#dc2626',
    '#7c3aed', '#059669', '#d97706', '#6b7280', '#000000',
  ];

  const fontSizes = [
    { label: 'Small', value: '2' },
    { label: 'Normal', value: '3' },
    { label: 'Medium', value: '4' },
    { label: 'Large', value: '5' },
    { label: 'Extra Large', value: '6' },
  ];

  // Generate preview HTML with variables replaced
  const getPreviewHtml = () => {
    let bodyHtml = value;
    if (previewEvent) {
      const vars = buildVariables(previewEvent, previewVenue);
      bodyHtml = replaceVariables(bodyHtml, vars);
    }
    return generateBrandedEmail(bodyHtml, subject);
  };

  const ToolbarButton = ({ icon: Icon, active, onClick, title, size = 16 }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={`p-1.5 rounded transition ${
        active
          ? 'bg-emerald-100 text-emerald-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      <Icon size={size} />
    </button>
  );

  const ToolbarDivider = () => (
    <div className="w-px h-6 bg-gray-200 mx-1" />
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Subject field */}
      {onSubjectChange && (
        <div className="border-b border-gray-200 px-4 py-3">
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            placeholder="Email subject line..."
            className="w-full text-gray-900 font-medium text-base focus:outline-none placeholder-gray-400"
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-3 py-2 border-b border-gray-200 bg-gray-50 flex-wrap">
        {/* Undo/Redo */}
        <ToolbarButton icon={Undo2} onClick={() => execCommand('undo')} title="Undo" />
        <ToolbarButton icon={Redo2} onClick={() => execCommand('redo')} title="Redo" />
        <ToolbarDivider />

        {/* Font size dropdown */}
        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setShowFontSize(!showFontSize);
              setShowVariables(false);
              setShowColorPicker(false);
            }}
            title="Font size"
            className="flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:bg-gray-100 rounded transition text-sm"
          >
            <Type size={16} />
            <span className="text-xs">▼</span>
          </button>
          {showFontSize && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-32">
              {fontSizes.map((fs) => (
                <button
                  key={fs.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    execCommand('fontSize', fs.value);
                    setShowFontSize(false);
                  }}
                  className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {fs.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <ToolbarDivider />

        {/* Text formatting */}
        <ToolbarButton icon={Bold} active={activeFormats.bold} onClick={() => execCommand('bold')} title="Bold" />
        <ToolbarButton icon={Italic} active={activeFormats.italic} onClick={() => execCommand('italic')} title="Italic" />
        <ToolbarButton icon={Underline} active={activeFormats.underline} onClick={() => execCommand('underline')} title="Underline" />
        <ToolbarDivider />

        {/* Text color */}
        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setShowColorPicker(!showColorPicker);
              setShowVariables(false);
              setShowFontSize(false);
            }}
            title="Text color"
            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition"
          >
            <Palette size={16} />
          </button>
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-2 grid grid-cols-5 gap-1">
              {colors.map((color) => (
                <button
                  key={color}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    execCommand('foreColor', color);
                    setShowColorPicker(false);
                  }}
                  className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          )}
        </div>
        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton icon={AlignLeft} active={activeFormats.justifyLeft} onClick={() => execCommand('justifyLeft')} title="Align left" />
        <ToolbarButton icon={AlignCenter} active={activeFormats.justifyCenter} onClick={() => execCommand('justifyCenter')} title="Align center" />
        <ToolbarButton icon={AlignRight} active={activeFormats.justifyRight} onClick={() => execCommand('justifyRight')} title="Align right" />
        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton icon={List} onClick={() => execCommand('insertUnorderedList')} title="Bullet list" />
        <ToolbarButton icon={ListOrdered} onClick={() => execCommand('insertOrderedList')} title="Numbered list" />
        <ToolbarDivider />

        {/* Link & Image */}
        <ToolbarButton icon={Link} onClick={insertLink} title="Insert link" />
        <ToolbarButton icon={Image} onClick={insertImage} title="Insert image" />
        <ToolbarDivider />

        {/* Insert Variable */}
        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setShowVariables(!showVariables);
              setShowColorPicker(false);
              setShowFontSize(false);
            }}
            title="Insert variable"
            className="flex items-center gap-1 px-2 py-1.5 text-blue-600 hover:bg-blue-50 rounded transition text-sm font-medium"
          >
            <Code size={14} />
            Insert Variable
          </button>
          {showVariables && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 py-1 max-h-64 overflow-y-auto" style={{ width: 280 }}>
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                Click to insert
              </p>
              {TEMPLATE_VARIABLES.map((v) => (
                <button
                  key={v.key}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertVariable(v.key);
                  }}
                  className="block w-full text-left px-3 py-2 hover:bg-blue-50 transition"
                >
                  <span className="text-sm font-medium text-gray-900">{v.label}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {`{${v.key}}`} → {v.example}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Preview toggle */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setShowPreview(!showPreview);
          }}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium transition ${
            showPreview
              ? 'bg-emerald-100 text-emerald-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Eye size={14} />
          Preview
        </button>
      </div>

      {/* Editor or Preview */}
      {showPreview ? (
        <div className="bg-gray-100 p-4" style={{ minHeight }}>
          <div className="bg-white rounded shadow max-w-xl mx-auto">
            <iframe
              title="Email Preview"
              srcDoc={getPreviewHtml()}
              className="w-full border-0 rounded"
              style={{ minHeight: minHeight + 100 }}
            />
          </div>
          {previewEvent && (
            <p className="text-center text-xs text-gray-500 mt-3">
              Preview using: {previewEvent.name} ({previewEvent.client})
            </p>
          )}
          {!previewEvent && (
            <p className="text-center text-xs text-gray-500 mt-3">
              Variables shown as placeholders. Select an event for a live preview.
            </p>
          )}
        </div>
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyUp={updateActiveFormats}
          onMouseUp={updateActiveFormats}
          className="px-4 py-3 text-gray-900 text-sm leading-relaxed focus:outline-none"
          style={{ minHeight }}
          data-placeholder="Start writing your email..."
          suppressContentEditableWarning
        />
      )}
    </div>
  );
};

export default EmailEditor;
